<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-0" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <SupportHero v-if="!admin" compact title="Tickets" icon="mdi-ticket-outline" />
      <div class="pa-4">
      <v-progress-linear v-if="loading" indeterminate />
      <v-alert v-else-if="!ticket" type="error" text class="ma-4">Ticket introuvable.</v-alert>
      <v-row v-else class="pa-2">
        <v-col cols="12" md="8">
          <div class="text-caption grey--text">{{ ticket.reference }} · {{ ticket.author.username }} · {{ formatDate(ticket.created_at) }}</div>
          <h1 class="mb-2">{{ ticket.subject }}</h1>
          <p style="white-space: pre-wrap">{{ ticket.description }}</p>

          <div class="mb-4">
            <LinkedObjectChip v-for="l in links" :key="l.id" :link="l" :admin="admin"
              :closable="canEditLinks" class="mr-1 mb-1" @remove="removeLink" />
          </div>

          <h3 class="mb-2">Échanges</h3>
          <v-card v-for="c in comments" :key="c.id" outlined class="mb-2 pa-3"
            :class="{ 'internal-note': c.is_internal }">
            <div class="text-caption grey--text">
              {{ c.author.username }} · {{ formatDate(c.created_at) }}
              <v-chip v-if="c.is_internal" x-small color="warning" class="ml-1">Note interne</v-chip>
            </div>
            <div style="white-space: pre-wrap">{{ c.body }}</div>
          </v-card>
          <div v-if="!comments.length" class="grey--text mb-2">Aucun échange.</div>

          <v-textarea v-model="body" label="Votre réponse" rows="3" auto-grow outlined dense hide-details class="mt-4" />
          <div class="d-flex align-center mt-2">
            <v-checkbox v-if="staffMode" v-model="internal" label="Note interne" dense hide-details class="mt-0" />
            <v-spacer />
            <v-btn color="primary" elevation="0" :disabled="!body.trim()" :loading="sending" @click="send">Envoyer</v-btn>
          </div>
        </v-col>

        <v-col cols="12" md="4">
          <TicketActivityCard :ticket="ticket" :user-actions="!staffMode" @status="patch({ status: $event })">
            <template v-if="staffMode">
              <v-select :value="ticket.status" :items="statusItems" label="Statut" outlined dense @change="patch({ status: $event })" />
              <v-select :value="ticket.priority" :items="priorities" label="Priorité" outlined dense @change="patch({ priority: $event })" />
              <v-select :value="ticket.category" :items="categories" label="Catégorie" outlined dense @change="patch({ category: $event })" />
              <v-btn small text color="primary" @click="patch({ assignee: 'me' })" v-if="!ticket.assignee || ticket.assignee.username !== myName">
                M'assigner
              </v-btn>
              <div v-if="ticket.assignee" class="text-caption">Assigné à {{ ticket.assignee.username }}</div>
            </template>
          </TicketActivityCard>
          <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">{{ error }}</v-alert>
        </v-col>
      </v-row>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import SupportHero from '@/components/support/SupportHero.vue'
import LinkedObjectChip from '@/components/support/LinkedObjectChip.vue'
import TicketActivityCard from '@/components/support/TicketActivityCard.vue'
import { CATEGORIES } from '@/components/support/SupportNewDialog.vue'
import {
  fetchTicket, updateTicket, addTicketComment, removeTicketLink,
} from '@/services/supportApi'
import { STATUS_ITEMS, formatDate } from '@/utils/support'

export default {
  name: 'SupportTicketView',
  components: { PageBack, SupportHero, MainLayout, LinkedObjectChip, TicketActivityCard },
  props: {
    /** Staff triage mode (/admin/tickets/:id): status, assignee and internal notes. */
    admin: { type: Boolean, default: false },
  },
  data: () => ({
    ticket: null,
    loading: true,
    body: '',
    internal: false,
    sending: false,
    error: '',
    statusItems: STATUS_ITEMS,
    categories: CATEGORIES,
    priorities: [
      { value: 'low', text: 'Basse' },
      { value: 'normal', text: 'Normale' },
      { value: 'high', text: 'Haute' },
    ],
  }),
  computed: {
    isStaff() { return this.$store.getters['settings/isStaff'] },
    /** Staff controls only exist in the admin space; the support space is the plain user view. */
    staffMode() { return this.admin && this.isStaff },
    /** Back-office-backed links (agent session…) are for the admin space only. */
    links() {
      return this.ticket.links.filter((l) => this.admin || !l.admin_url)
    },
    comments() {
      return this.ticket.comments.filter((c) => this.staffMode || !c.is_internal)
    },
    myName() { return this.$store.getters['settings/userName'] },
    canEditLinks() { return this.staffMode },
  },
  watch: { '$route.params.id': 'load' },
  created() { this.load() },
  methods: {
    formatDate,
    async load() {
      this.loading = true
      try {
        this.ticket = await fetchTicket(this.$route.params.id)
        // Staff opening someone else's ticket (e.g. from a notification) land in the admin space
        if (!this.admin && this.isStaff && !this.ticket.is_author) {
          this.$router.replace({ name: 'admin_ticket_detail', params: { id: this.ticket.id } })
        }
      } catch (e) {
        this.ticket = null
      } finally {
        this.loading = false
      }
    },
    async patch(data) {
      this.error = ''
      try {
        this.ticket = await updateTicket(this.ticket.id, data)
        this.$store.dispatch('support/loadCounts')
      } catch (e) {
        this.error = 'Action non autorisée ou impossible.'
      }
    },
    async send() {
      this.sending = true
      try {
        await addTicketComment(this.ticket.id, this.body.trim(), this.staffMode && this.internal)
        this.body = ''
        this.internal = false
        this.ticket = await fetchTicket(this.ticket.id)
      } catch (e) {
        this.error = "Impossible d'envoyer le message."
      } finally {
        this.sending = false
      }
    },
    async removeLink(link) {
      try {
        await removeTicketLink(this.ticket.id, link.id)
        this.ticket = await fetchTicket(this.ticket.id)
      } catch (e) {
        this.error = 'Impossible de retirer le lien.'
      }
    },
  },
}
</script>

<style scoped>
.internal-note { background: rgba(255, 193, 7, 0.1); }
</style>
